import {ComboboxRoot} from "@base-ui/react";
import debounce from "lodash-es/debounce";
import {ChevronDown, Loader2, SearchIcon} from "lucide-react";
import {ReactNode, useMemo, useRef, type KeyboardEvent} from "react";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxValue,
  GroupedItem,
  blurActiveTextInput,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import {Item, ItemContent, ItemDescription} from "@/components/ui/item";

import {InputGroupAddon} from "./input-group";

function isEnterLikeKey(e: KeyboardEvent<HTMLInputElement>): boolean {
  if (e.nativeEvent.isComposing) return false;
  if (e.key === "Enter" || e.key === "NumpadEnter") return true;
  // Android / legacy WebKit often leave `key` empty while keyCode is 13.
  return e.keyCode === 13;
}

type AsyncSearchComboboxGroupProps<T> = {
  grouped: true;
  items: GroupedItem<T>[];
};

type AsyncSearchComboboxSimpleProps<T> = {
  grouped?: false;
  items: T[];
};

type AsyncSearchComboboxSingleProps<T> = {
  value: T | null;
  multiple?: false;
  onChange: (value: T | null, eventDetails: ComboboxRoot.ChangeEventDetails) => void;
};

type AsyncSearchComboboxMultipleProps<T> = {
  value: T[];
  multiple: true;
  onChange: (value: T[], eventDetails: ComboboxRoot.ChangeEventDetails) => void;
};

type AsyncSearchComboboxProps<T> = (
  | AsyncSearchComboboxSingleProps<T>
  | AsyncSearchComboboxMultipleProps<T>
) &
  (AsyncSearchComboboxSimpleProps<T> | AsyncSearchComboboxGroupProps<T>) & {
    // Search & pagination
    onSearch: (term: string) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;

    // Loading states
    isLoading?: boolean;
    isLoadingMore?: boolean;

    // Item accessors (enforced by generic T)
    getItemKey: (item: T) => string | number;
    getItemLabel: (item: T) => string;
    isItemEqual: (item: T, selected: T | null) => boolean;

    // Customization
    renderItem?: (item: T) => ReactNode;
    placeholder?: string;
    emptyMessage?: string;
    loadingMessage?: string;
    loadingMoreMessage?: string;
    debounceMs?: number;
    disabled?: boolean;
    className?: string;
    showClear?: boolean;
    shouldRenderSelection?: boolean;
    /** When false, Tab/Enter will not auto-pick a single dropdown row. Default true. */
    allowKeyboardAutoSelect?: boolean;
    /** Enter with no matching row — e.g. switch to manual entry with the typed term. */
    onEnterWithoutMatch?: (term: string) => void;
  } & Omit<
    React.ComponentProps<typeof Combobox>,
    | "items"
    | "value"
    | "filteredItems"
    | "itemToStringLabel"
    | "isItemEqualToValue"
    | "onValueChange"
  >;

export function AsyncSearchCombobox<T>({
  shouldRenderSelection = true,
  items,
  value,
  onChange,
  onSearch,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  isLoadingMore = false,
  getItemKey,
  getItemLabel,
  isItemEqual,
  renderItem,
  placeholder = "Search...",
  emptyMessage = "No results found.",
  loadingMessage = "Loading...",
  loadingMoreMessage = "Loading more...",
  debounceMs = 300,
  disabled = false,
  multiple = false,
  grouped = false,
  className,
  showClear = true,
  allowKeyboardAutoSelect = true,
  onEnterWithoutMatch,
  filter: filterProp,
  autoHighlight: autoHighlightProp,
  onOpenChange: onOpenChangeProp,
  ...comboboxProps
}: AsyncSearchComboboxProps<T>) {
  const anchor = useComboboxAnchor();
  const pendingKeyboardDismissRef = useRef(false);

  const handleDebounceSearch = useMemo(
    () => debounce((term: string) => onSearch(term), debounceMs),
    [onSearch, debounceMs],
  );

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (grouped || multiple || disabled || !allowKeyboardAutoSelect) return;
    const isTab = e.key === "Tab" && !e.shiftKey;
    const isEnter = isEnterLikeKey(e);
    if (!isTab && !isEnter) return;
    if (value != null) return;
    const list = comboboxItems as T[];
    const q = e.currentTarget.value.trim();
    const qLower = q.toLowerCase();
    let pick: T | null = null;
    if (q) {
      pick = list.find((i) => getItemLabel(i).trim().toLowerCase() === qLower) ?? null;
    } else if (list.length === 1) {
      pick = list[0]!;
    }
    if (!pick) {
      if (isEnter && onEnterWithoutMatch) {
        if (q) {
          e.preventDefault();
          e.stopPropagation();
          handleDebounceSearch.cancel();
          onEnterWithoutMatch(q);
        } else {
          // Block implicit form submit on mobile when Enter does not apply.
          e.preventDefault();
        }
      }
      return;
    }
    e.preventDefault();
    if (isEnter) {
      e.stopPropagation();
    }
    pendingKeyboardDismissRef.current = true;
    (onChange as AsyncSearchComboboxSingleProps<T>["onChange"])(pick, {
      reason: "item-press",
    } as ComboboxRoot.ChangeEventDetails);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;

    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      onLoadMore();
    }
  };

  const comboboxItems = useMemo(() => {
    if (grouped || multiple || value == null) return items;
    const selected = value as T;
    const list = items as T[];
    if (list.some((item) => isItemEqual(item, selected))) return list;
    return [selected, ...list];
  }, [grouped, multiple, items, value, isItemEqual]);

  return (
    <Combobox
      multiple={multiple}
      items={comboboxItems}
      itemToStringLabel={(item: unknown) => getItemLabel(item as T)}
      isItemEqualToValue={(item: unknown, selected: unknown) =>
        isItemEqual(item as T, selected as T | null)
      }
      value={value}
      filter={filterProp !== undefined ? filterProp : null}
      autoHighlight={autoHighlightProp ?? (!multiple && !grouped ? true : undefined)}
      onValueChange={(value: unknown, eventDetails: ComboboxRoot.ChangeEventDetails) => {
        if (disabled) return;
        if (eventDetails.reason === "none") return;
        if (multiple) {
          (onChange as AsyncSearchComboboxMultipleProps<T>["onChange"])(value as T[], eventDetails);
        } else {
          pendingKeyboardDismissRef.current = true;
          (onChange as AsyncSearchComboboxSingleProps<T>["onChange"])(
            value as T | null,
            eventDetails,
          );
        }
      }}
      onOpenChange={(open, eventDetails) => {
        onOpenChangeProp?.(open, eventDetails);
        if (open || multiple) return;
        if (!pendingKeyboardDismissRef.current) return;
        pendingKeyboardDismissRef.current = false;
        blurActiveTextInput();
      }}
      onInputValueChange={(v, e) => {
        if (disabled) return;
        const r = e.reason as string;
        if (r !== "input-change" && r !== "input-clear" && r !== "input-paste") {
          return;
        }
        if (v.trim().length === 0) {
          handleDebounceSearch.cancel();
          onSearch("");
          return;
        }
        if (debounceMs <= 0) {
          onSearch(v);
        } else {
          handleDebounceSearch(v);
        }
      }}
      {...comboboxProps}
      disabled={disabled}
    >
      {multiple ? (
        <ComboboxChips ref={anchor} className="w-full">
          <SearchIcon className="text-muted-foreground size-4" />
          <ComboboxValue>
            {(values: T[]) => (
              <>
                {shouldRenderSelection &&
                  values.map((value: T) => (
                    <ComboboxChip key={getItemKey(value)}>{getItemLabel(value)}</ComboboxChip>
                  ))}
                <ComboboxChipsInput placeholder={placeholder} disabled={disabled} />
                {isLoading ? (
                  <Loader2 className="text-muted-foreground size-4 animate-spin" />
                ) : disabled ? null : (
                  <ChevronDown className="text-muted-foreground size-4" />
                )}
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>
      ) : (
        <ComboboxInput
          showClear={showClear && !disabled}
          showTrigger={!disabled}
          // value={value ? value.}
          groupRef={anchor}
          isLoading={isLoading}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          enterKeyHint={onEnterWithoutMatch ? "search" : undefined}
          onKeyDownCapture={handleInputKeyDown}
        >
          {!value ? (
            <InputGroupAddon align="inline-start">
              <SearchIcon className="size-4" />
            </InputGroupAddon>
          ) : null}
        </ComboboxInput>
      )}
      <ComboboxContent anchor={anchor}>
        {!isLoading && !isLoadingMore ? <ComboboxEmpty>{emptyMessage}</ComboboxEmpty> : null}
        <ComboboxList onScroll={handleScroll}>
          {(item: T | GroupedItem<T>, index: number) =>
            grouped
              ? getItems({
                  grouped,
                  group: item as GroupedItem<T>,
                  index,
                  totalItems: items.length,
                  getItemKey,
                  getItemLabel,
                  renderItem,
                })
              : getItems({
                  grouped: false,
                  item: item as T,
                  index,
                  totalItems: items.length,
                  getItemKey,
                  getItemLabel,
                  renderItem,
                })
          }
        </ComboboxList>
        {isLoading && (
          <Item>
            <ItemContent>
              <ItemDescription className="flex items-center gap-2 text-xs">
                {isLoadingMore ? loadingMoreMessage : loadingMessage}
                <Loader2 className="size-4 animate-spin" />
              </ItemDescription>
            </ItemContent>
          </Item>
        )}
      </ComboboxContent>
    </Combobox>
  );
}

type GetItemsProps<T> = (
  | {
      grouped: true;
      group: GroupedItem<T>;
      item?: undefined;
    }
  | {
      grouped?: false;
      item: T;
      group?: undefined;
    }
) & {
  index: number;
  totalItems: number;
  getItemKey: (item: T) => string | number;
  getItemLabel: (item: T) => string;
  renderItem?: (item: T) => ReactNode;
};

function getItems<T>({
  grouped,
  group,
  item,
  index,
  totalItems,
  getItemKey,
  getItemLabel,
  renderItem,
}: GetItemsProps<T>) {
  if (grouped) {
    return (
      <ComboboxGroup key={`group-${group.value}`} items={group.items}>
        <ComboboxLabel>{group.value}</ComboboxLabel>
        <ComboboxCollection>
          {(item) => (
            <ComboboxItem key={getItemKey(item)} value={item}>
              {renderItem ? renderItem(item) : getItemLabel(item)}
            </ComboboxItem>
          )}
        </ComboboxCollection>
        {index < totalItems - 1 && <ComboboxSeparator />}
      </ComboboxGroup>
    );
  } else {
    return (
      <ComboboxItem key={getItemKey(item)} value={item}>
        {renderItem ? renderItem(item) : getItemLabel(item)}
      </ComboboxItem>
    );
  }
}
